/**
 * 客户端沙箱 — 资源限制 + 操作审计 + 隔离
 */

export interface SandboxLimits {
  maxMemoryMB?: number
  maxCpuPercent?: number
  maxFileOps?: number
  maxNetworkReqs?: number
  allowedPaths?: string[]
  blockedCommands?: string[]
}

export interface SandboxAuditEntry {
  action: string
  resource?: string
  timestamp: number
  allowed: boolean
  reason?: string
}

/**
 * 客户端沙箱管理器
 */
export class ClientSandbox {
  private limits: Required<SandboxLimits>
  private counters = { fileOps: 0, networkReqs: 0 }
  private audit: SandboxAuditEntry[] = []
  private maxAudit = 1000

  constructor(limits: SandboxLimits = {}) {
    this.limits = {
      maxMemoryMB: limits.maxMemoryMB ?? 512,
      maxCpuPercent: limits.maxCpuPercent ?? 80,
      maxFileOps: limits.maxFileOps ?? 1000,
      maxNetworkReqs: limits.maxNetworkReqs ?? 100,
      allowedPaths: limits.allowedPaths ?? ['/tmp', '/var/remotekit'],
      blockedCommands: limits.blockedCommands ?? ['rm -rf /', 'format', 'shutdown'],
    }
  }

  /** 检查文件操作是否允许 */
  checkFileAccess(path: string): { allowed: boolean; reason?: string } {
    if (this.counters.fileOps >= this.limits.maxFileOps) {
      return this.deny('file:access', path, 'file operation limit exceeded')
    }
    const allowed = this.limits.allowedPaths.some((p) => path.startsWith(p))
    if (!allowed) return this.deny('file:access', path, `path not in allowlist`)
    this.counters.fileOps++
    this.log('file:access', path, true)
    return { allowed: true }
  }

  /** 检查命令是否允许 */
  checkCommand(command: string): { allowed: boolean; reason?: string } {
    const blocked = this.limits.blockedCommands.some((b) => command.includes(b))
    if (blocked) return this.deny('command:exec', command, 'command blocked')
    this.log('command:exec', command, true)
    return { allowed: true }
  }

  /** 检查网络请求 */
  checkNetwork(target: string): { allowed: boolean; reason?: string } {
    if (this.counters.networkReqs >= this.limits.maxNetworkReqs) {
      return this.deny('network:request', target, 'network request limit exceeded')
    }
    this.counters.networkReqs++
    this.log('network:request', target, true)
    return { allowed: true }
  }

  /** 检查内存使用 */
  checkMemory(): { allowed: boolean; usageMB: number } {
    const usageMB = process.memoryUsage?.().heapUsed / 1024 / 1024 || 0
    const allowed = usageMB <= this.limits.maxMemoryMB
    if (!allowed) this.log('memory:check', `${usageMB.toFixed(0)}MB`, false, 'memory limit exceeded')
    return { allowed, usageMB }
  }

  /** 获取审计日志 */
  getAuditLog(limit = 100): SandboxAuditEntry[] {
    return this.audit.slice(-limit)
  }

  /** 获取当前计数器 */
  getCounters(): typeof this.counters {
    return { ...this.counters }
  }

  /** 重置计数器 */
  resetCounters(): void {
    this.counters = { fileOps: 0, networkReqs: 0 }
  }

  /** 获取限制配置 */
  getLimits(): Required<SandboxLimits> {
    return { ...this.limits }
  }

  private deny(action: string, resource: string, reason: string): { allowed: boolean; reason: string } {
    this.log(action, resource, false, reason)
    return { allowed: false, reason }
  }

  private log(action: string, resource: string | undefined, allowed: boolean, reason?: string): void {
    this.audit.push({ action, resource, timestamp: Date.now(), allowed, reason })
    if (this.audit.length > this.maxAudit) this.audit.shift()
  }
}
